import React, { useRef, useCallback, useState } from 'react';
import { Upload, Video, Play, Info, X } from 'lucide-react';
import { SUPPORTED_VIDEO_FORMATS } from '../types';

interface VideoUploadAreaProps {
  onFileSelect: (files: FileList) => void;
}

export default function VideoUploadArea({ onFileSelect }: VideoUploadAreaProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [showFormatsModal, setShowFormatsModal] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    
    if (e.dataTransfer.files) {
      onFileSelect(e.dataTransfer.files);
    }
  }, [onFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Input onChange disparado, arquivos selecionados:', e.target.files?.length);
    if (e.target.files) {
      onFileSelect(e.target.files);
      // Limpar o valor do input para permitir re-seleção do mesmo arquivo
      e.target.value = '';
      console.log('Input value limpo para permitir re-seleção');
    }
  }, [onFileSelect]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const isDarkMode = document.documentElement.classList.contains('dark');

  return (
    <div className="w-full">
      {/* Área de Upload Principal - Mobile First */}
      <div 
        className={`relative ${
          isDarkMode 
            ? `bg-[#383D52] border-[#4A4F64] ${isDragActive ? 'border-red-400 bg-[#4A4F64]' : 'border-dashed border-2'}`
            : `bg-white border-[#E5E5E5] ${isDragActive ? 'border-red-400 bg-red-50' : 'border-dashed border-2'}`
        } rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 md:p-6 lg:p-8 transition-all duration-300 cursor-pointer hover:shadow-lg`}
        onDrop={handleDrop}
        onDragOver={handleDragEnter}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="text-center">
          {/* Ícone Principal - Mobile First */}
          <div className={`relative mx-auto mb-3 sm:mb-4 md:mb-6 transition-all duration-300 ${
            isDragActive ? 'scale-110' : 'hover:scale-105'
          }`}>
            <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-red-500 rounded-lg flex items-center justify-center mx-auto relative">
              <Video className={`w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-white transition-transform duration-300 ${
                isDragActive ? 'animate-bounce' : ''
              }`} />
              <Play className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-white absolute bottom-0.5 right-0.5 sm:bottom-1 sm:right-1" />
            </div>
          </div>

          {/* Texto Principal - Mobile First */}
          <h3 className={`text-lg font-medium mb-2 transition-colors duration-300 ${
            isDragActive
              ? isDarkMode ? 'text-red-300' : 'text-red-600'
              : isDarkMode ? 'text-white hover:text-red-300' : 'text-gray-900 hover:text-red-600'
          }`}>
            {isDragActive ? 'Solte seu vídeo aqui' : 'Nenhum vídeo carregado'}
          </h3>
          <p className={`text-sm transition-colors duration-300 ${
            isDragActive
              ? isDarkMode ? 'text-red-400' : 'text-red-500'
              : isDarkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-600 hover:text-red-500'
          }`}>
            {isDragActive ? 'Solte para adicionar à lista' : 'Arraste e solte seu vídeo ou clique para selecionar'}
          </p>

          {/* Botão Estilizado - Mobile First */}
          <button className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-3 sm:px-4 md:px-6 lg:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 mb-3 sm:mb-4 md:mb-6">
            <div className="flex items-center justify-center space-x-1.5 sm:space-x-2">
              <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" />
              <span className="text-xs sm:text-sm md:text-base">Escolher vídeo</span>
            </div>
          </button>

          {/* Input Oculto */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="video/*,.mp4,.webm,.ogv,.m4v,.mov,.avi,.mkv,.flv,.wmv,.3gp,.divx,.xvid,.mpg,.mpeg,.m2v,.ts,.mts,.m2ts,.vob,.f4v,.asf,.rm,.rmvb"
            onChange={handleFileInputChange}
            className="hidden"
          />

          {/* Formatos Suportados - Mobile First com Scroll Horizontal */}
          <div className="mb-2 sm:mb-3 md:mb-4">
            {/* Mobile: Scroll horizontal */}
            <div className="flex sm:hidden overflow-x-auto scrollbar-hide gap-1.5 pb-2 px-1">
              {['mp4', 'webm', 'mov', 'avi', 'mkv'].map((format, index) => (
                <span 
                  key={format}
                  className={`px-2 py-1 text-xs font-medium rounded-lg transition-all duration-300 whitespace-nowrap ${
                    format === 'mp4' || format === 'webm'
                      ? `${isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-600'} border border-green-300/30`
                      : `${isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-600'} border border-red-300/30`
                  } hover:scale-105 cursor-pointer`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {format.toUpperCase()}
                </span>
              ))}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFormatsModal(true);
                }}
                className={`px-2 py-1 text-xs font-medium rounded-lg transition-all duration-300 whitespace-nowrap ${
                  isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-600'
                } border border-blue-300/30 hover:scale-105 cursor-pointer flex items-center space-x-1`}
              >
                <Info className="w-3 h-3" />
                <span>+</span>
              </button>
            </div>

            {/* Desktop: Flex wrap */}
            <div className="hidden sm:flex flex-wrap justify-center gap-1.5 md:gap-2">
              {SUPPORTED_VIDEO_FORMATS.map((format, index) => (
                <span 
                  key={format}
                  className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-lg transition-all duration-300 ${
                    format === 'mp4' || format === 'webm'
                      ? `${isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-600'} border border-green-300/30`
                      : `${isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-600'} border border-red-300/30`
                  } hover:scale-105 cursor-pointer`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {format.toUpperCase()}
                </span>
              ))}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFormatsModal(true);
                }}
                className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-lg transition-all duration-300 ${
                  isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-600'
                } border border-blue-300/30 hover:scale-105 cursor-pointer flex items-center space-x-1`}
              >
                <Info className="w-3 h-3" />
                <span className="hidden sm:inline">Mais info</span>
                <span className="sm:hidden">+</span>
              </button>
            </div>
          </div>
          
          <div className={`text-xs sm:text-sm px-2 ${
            isDarkMode ? 'text-[#EAECF3]/50' : 'text-[#4A4A69]/50'
          }`}>
            Suporte para os principais formatos de vídeo
          </div>
        </div>
      </div>

      {/* Modal de Formatos Suportados */}
      {showFormatsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="border rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" style={{ backgroundColor: '#383D52', borderColor: '#4A4F64' }}>
            {/* Header */}
            <div className={`p-6 border-b ${
              isDarkMode ? 'border-[#4A4F64]' : 'border-[#E5E5E5]'
            } flex items-center justify-between`}>
              <h3 className={`text-xl font-semibold ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                Formatos de Vídeo Suportados
              </h3>
              <button
                onClick={() => setShowFormatsModal(false)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-[#383D52]' : 'hover:bg-[#F8F9FA]'
                }`}
              >
                <X className={`w-5 h-5 ${
                  isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                }`} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SUPPORTED_VIDEO_FORMATS.map((format) => (
                  <div
                    key={format}
                    className={`p-4 rounded-lg ${
                      isDarkMode ? 'bg-[#383D52]' : 'bg-[#F8F9FA]'
                    } hover:scale-105 transition-transform duration-200`}
                  >
                    <div className={`font-medium text-lg uppercase mb-2 ${
                      isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                    }`}>
                      {format}
                    </div>
                    <div className={`text-sm ${
                      isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
                    }`}>
                      {format === 'mp4' && 'Formato mais popular e compatível'}
                      {format === 'webm' && 'Formato otimizado para web'}
                      {format === 'ogv' && 'Formato open source'}
                      {format === 'm4v' && 'Formato Apple/iTunes'}
                      {format === 'mov' && 'Formato QuickTime'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 